import omit from 'lodash/omit';

import { types as sdkTypes } from '../../util/sdkLoader';
import { uniqueBy } from '../../util/generators';
import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { parse } from '../../util/urlHelpers';
import { isUserAuthorized } from '../../util/userHelpers';

import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import axios from 'axios';
import { fetchCurrentUser } from '../../ducks/user.duck';
import { getUploadURL } from '../../util/api';

const { UUID } = sdkTypes;

// Create array of N items where indexing starts from 1
const getArrayOfNItems = n =>
  Array(n)
    .fill()
    .map((v, i) => i + 1)
    .slice(1);

const requestAction = actionType => params => ({ type: actionType, payload: { params } });

const successAction = actionType => result => ({ type: actionType, payload: result.data });

const errorAction = actionType => payload => ({ type: actionType, payload, error: true });

// ================ Action types ================ //

export const UPLOAD_FILE_REQUEST = 'app/EditListingPage/UPLOAD_FILE_REQUEST';
export const UPLOAD_FILE_SUCCESS = 'app/EditListingPage/UPLOAD_FILE_SUCCESS';
export const UPLOAD_FILE_ERROR = 'app/EditListingPage/UPLOAD_FILE_ERROR';
export const REMOVE_LISTING_FILE = 'app/EditListingPage/REMOVE_LISTING_FILE';

// ================ Reducer ================ //

const initialState = {};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case UPLOAD_FILE_REQUEST: {
      // payload.params: { id: 'tempId', file }
      const uploadedFiles = {
        ...state.uploadedFiles,
        [payload.params.id]: { ...payload.params },
      };
      return {
        ...state,
        uploadedFiles,
        uploadedFilesOrder: state.uploadedFilesOrder.concat([payload.params.id]),
        uploadFileError: null,
      };
    }
    case UPLOAD_FILE_SUCCESS: {
      // payload.params: { id: 'tempId', imageId: 'some-real-id', attributes, type }
      const { id, url, ...rest } = payload;
      const uploadedFiles = { ...state.uploadedFiles, [id]: { id, url, ...rest } };
      return { ...state, uploadedFiles };
    }
    case UPLOAD_FILE_ERROR: {
      // eslint-disable-next-line no-console
      const { id, error } = payload;
      const uploadedFilesOrder = state.uploadedFilesOrder.filter(i => i !== id);
      const uploadedFiles = omit(state.uploadedFiles, id);
      return { ...state, uploadedFilesOrder, uploadedFiles, uploadFileError: error };
    }

    case REMOVE_LISTING_FILE: {
      const id = payload.id;
      const removedFilesIds = state.uploadedFiles[id]
        ? state.removedFilesIds
        : state.removedFilesIds.concat(id);
      const uploadedFiles = omit(state.uploadedFiles, id);
      const uploadedFilesOrder = state.uploadedFilesOrder.filter(i => i !== id);

      return { ...state, uploadedFiles, uploadedFilesOrder, removedFilesIds };
    }

    default:
      return state;
  }
}

// ================ Selectors ================ //

// ================ Action creators ================ //

export const removeListingFile = id => ({
  type: REMOVE_LISTING_FILE,
  payload: { id },
});

export const uploadFileRequest = requestAction(UPLOAD_FILE_REQUEST);
export const uploadFileSuccess = successAction(UPLOAD_FILE_SUCCESS);
export const uploadFileError = errorAction(UPLOAD_FILE_ERROR);

// ================ Thunk ================ //

export function requestFileUpload(actionPayload) {
  return (dispatch, getState, sdk) => {
    dispatch(uploadFileRequest(actionPayload));
    return getUploadURL({ key: `listings/documents/${actionPayload.id}`, type: actionPayload.type })
      .then(response => {
        const { url } = response;
        if (!!url) {
          return axios
            .put(url, actionPayload.file, {
              headers: { 'Content-Disposition': 'inline', 'Content-Type': actionPayload.type },
            })
            .then(
              res => {
                const assetUrl = new URL(url);
                const fileUrl = `${assetUrl.origin}${assetUrl.pathname}`;
                return dispatch(
                  uploadFileSuccess({
                    data: { id: actionPayload.id, url: fileUrl, file: actionPayload.file },
                  })
                );
              },
              error => {
                return dispatch(uploadFileError(error));
              }
            );
        } else {
          return dispatch(uploadFileError('Failed'));
        }
      })
      .catch(e => {
        return dispatch(uploadFileError(e));
      });
  };
}

export const loadData = (params, search, config) => (dispatch, getState, sdk) => {
  return dispatch(fetchCurrentUser())
    .then(response => {
      return response;
    })
    .catch(e => {
      throw e;
    });
};
